/*
  Animated open/close for the OraLux accordions - the FAQ and the About page's principles
  list both run on this file.

  The markup stays a real <details>/<summary>, so with this file blocked or still loading
  the accordion is fully usable - the script only takes over the height transition that
  <details> cannot do on its own.

  Two deliberate choices:

  - The listener is delegated from the document and guarded by a single global flag, so a
    section re-render in the theme editor cannot leave a second listener behind, and newly
    inserted questions work without re-initialising anything.

  - The reduced-motion preference is read at the moment of the click, not captured at load.
    A value read once goes stale the moment the visitor changes the system setting with the
    page still open.

  - Rows are found by element rather than by class - any <summary> inside a
    [data-oralux-faq] root, with its panel marked [data-oralux-panel] - so a second section
    opts in with two attributes instead of a second copy of this logic. The FAQ's original
    .oralux-faq__panel is still accepted, so nothing there had to change.

  - A root marked [data-oralux-exclusive] closes its other open rows as one opens, and the two
    animations run together. This is done here rather than with the native `name` attribute on
    <details>: the browser closes the sibling the instant the clicked row's `open` flips, which
    puts the sibling's panel in a content-visibility subtree with nothing left to animate. The
    trade is that without this file several rows can be open at once, which is harmless.
*/
(function () {
  'use strict';

  if (window.oraluxFaqBound) return;
  window.oraluxFaqBound = true;

  var DEFAULT_DURATION = 260;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function durationFor(item) {
    if (prefersReducedMotion()) return 0;
    var root = item.closest('[data-oralux-faq]');
    var declared = root ? parseInt(root.getAttribute('data-duration'), 10) : NaN;
    return declared > 0 ? declared : DEFAULT_DURATION;
  }

  function animatePanel(panel, from, to, duration, onFinished) {
    if (panel.oraluxAnimation) panel.oraluxAnimation.cancel();

    panel.style.overflow = 'hidden';
    var animation = panel.animate(
      [{ height: from + 'px' }, { height: to + 'px' }],
      { duration: duration, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );
    panel.oraluxAnimation = animation;

    animation.addEventListener('finish', function () {
      panel.oraluxAnimation = null;
      panel.style.overflow = '';
      panel.style.height = '';
      onFinished();
    });

    /* A cancel means another click overtook this one; that click owns the state now. */
    animation.addEventListener('cancel', function () {
      panel.oraluxAnimation = null;
    });
  }

  function panelFor(item) {
    return item.querySelector('[data-oralux-panel], .oralux-faq__panel');
  }

  function closeItem(item, panel, duration) {
    item.setAttribute('data-faq-state', 'closing');
    animatePanel(panel, panel.getBoundingClientRect().height, 0, duration, function () {
      /*
        open is flipped here, on the animation actually finishing, rather than up front.
        Setting it early collapses the panel instantly and there is nothing left to animate.
      */
      item.open = false;
      item.setAttribute('data-faq-state', '');
    });
  }

  function closeSiblings(root, item, duration) {
    var others = root.querySelectorAll('details[open]');
    for (var i = 0; i < others.length; i++) {
      var other = others[i];
      if (other === item) continue;
      var otherPanel = panelFor(other);
      /* Its panel is on screen, so the measurement inside closeItem is trustworthy. */
      if (otherPanel) closeItem(other, otherPanel, duration);
      else other.open = false;
    }
  }

  function toggle(item, panel) {
    var duration = durationFor(item);
    var inFlight = Boolean(panel.oraluxAnimation);
    var isClosing = item.getAttribute('data-faq-state') === 'closing';
    /* Reopening mid-close counts as opening, otherwise the click would be swallowed. */
    var shouldOpen = !item.open || isClosing;

    if (shouldOpen) {
      var root = item.closest('[data-oralux-faq]');
      if (root && root.hasAttribute('data-oralux-exclusive')) {
        closeSiblings(root, item, duration);
      }

      /*
        Start from 0, not from a measurement. A closed <details> holds its content in a
        content-visibility: hidden subtree, where getBoundingClientRect keeps returning the
        size the panel had when it was last visible - so measuring here produced "animate
        from 87 to 87", and opening snapped instead of moving. The measurement is only
        trustworthy while an animation is genuinely in flight, because the element is still
        laid out then.
      */
      var from = inFlight ? panel.getBoundingClientRect().height : 0;
      item.open = true;
      item.setAttribute('data-faq-state', 'opening');
      animatePanel(panel, from, panel.scrollHeight, duration, function () {
        item.setAttribute('data-faq-state', '');
      });
      return;
    }

    /* Closing: the panel is on screen, so its box is real. */
    closeItem(item, panel, duration);
  }

  document.addEventListener('click', function (event) {
    var summary = event.target.closest('summary');
    if (!summary) return;

    /* Only accordions that asked for this, never the theme's own <details>. */
    if (!summary.closest('[data-oralux-faq]')) return;

    var item = summary.closest('details');
    if (!item) return;

    var panel = panelFor(item);
    /* No answer to reveal - leave the native behaviour alone. */
    if (!panel) return;

    event.preventDefault();
    toggle(item, panel);
  });
})();
